import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, HelpCircle, Boxes, LayoutDashboard, Search, FileText, ListChecks, Palette, StickyNote } from "lucide-react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { api } from "@/lib/api";
import { useHelp, moduleForPath } from "@/context/HelpContext";
import { contextLink, openNotesSidebar } from "@/lib/notesRouting";

const CAT_ICON = { module: Boxes, system: BookOpen, faq: HelpCircle };

// App routes reachable from the palette.
const ROUTES = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard },
  { label: "On-Page SEO Optimiser", to: "/seo", icon: Search },
  { label: "SEO Meta Editor", to: "/seo-meta", icon: FileText },
  { label: "FAQs Creator", to: "/faq", icon: ListChecks },
  { label: "Theme Design", to: "/theme-design", icon: Palette },
];

// ⌘K / Ctrl+K command palette: navigate the app + search Help in one place.
export const CommandPalette = () => {
  const { cmdOpen, setCmdOpen } = useHelp();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [noteHits, setNoteHits] = useState([]);

  useEffect(() => {
    if (!cmdOpen) { setQ(""); setHits([]); setNoteHits([]); return; }
  }, [cmdOpen]);

  useEffect(() => {
    if (!cmdOpen) return;
    const t = setTimeout(() => {
      api.get("/help/search", { params: { q, module_id: moduleForPath(location.pathname) } })
        .then((r) => setHits(r.data.slice(0, 6))).catch(() => setHits([]));
      if (q.trim().length >= 2) {
        api.get("/notes/search", { params: { q } })
          .then((r) => setNoteHits((r.data.results || []).slice(0, 6))).catch(() => setNoteHits([]));
      } else {
        setNoteHits([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, cmdOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (to) => { setCmdOpen(false); navigate(to); };
  const openHelp = (slug) => { setCmdOpen(false); navigate(`/help/${slug}`); };
  const openNote = (n) => {
    setCmdOpen(false);
    const link = contextLink(n.context);
    if (link) navigate(link);
    if (n.context?.kind === "module") setTimeout(() => openNotesSidebar(), 140);
  };

  const routeMatches = ROUTES.filter((r) => r.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
      <CommandInput value={q} onValueChange={setQ} placeholder="Search modules and help…" data-testid="command-input" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        {routeMatches.length > 0 && (
          <CommandGroup heading="Go to">
            {routeMatches.map((r) => {
              const Icon = r.icon;
              return (
                <CommandItem key={r.to} value={`go ${r.label}`} onSelect={() => go(r.to)} data-testid={`cmd-route-${r.to}`}>
                  <Icon className="mr-2 h-4 w-4 text-indigo-400" /> {r.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {hits.length > 0 && (
          <CommandGroup heading="Help">
            {hits.map((h) => {
              const Icon = CAT_ICON[h.category] || BookOpen;
              return (
                <CommandItem key={h.slug} value={`help ${h.title}`} onSelect={() => openHelp(h.slug)} data-testid={`cmd-help-${h.slug}`}>
                  <Icon className="mr-2 h-4 w-4 text-zinc-400" /> {h.title}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {noteHits.length > 0 && (
          <CommandGroup heading="Notes">
            {noteHits.map((n) => (
              <CommandItem key={n.note_id} value={`note ${n.note_id} ${n.content}`} onSelect={() => openNote(n)} data-testid={`cmd-note-${n.note_id}`}>
                <StickyNote className={`mr-2 h-4 w-4 ${n.resolved ? "text-emerald-400" : "text-amber-400"}`} />
                <span className="truncate">
                  <span className="text-zinc-200">{n.content}</span>
                  <span className="text-zinc-600"> · {n.context?.label || ""}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
